"use client";

import { useRef } from "react";
import { BUTTON_SECONDARY, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { LIKERT_SCALE_ANALYZER_STRINGS } from "@/components/tools/likert-scale-analyzer";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  type NumberFormatter,
  percentText,
} from "@/lib/tool-kit/research-design/format";
import {
  divergingSegments,
  type LikertItemSummary,
  type LikertScale,
  likertColors,
} from "@/lib/tool-kit/research-design/likert";
import { likertChartLayout } from "@/lib/tool-kit/research-design/likert-chart";

const CHART_FONT =
  "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * The diverging stacked bar chart, drawn as a self-contained SVG (white ground, explicit
 * fills and font) so the SVG and PNG downloads look exactly like the page. Each segment
 * carries a native tooltip with its label, share and count; the item table above is the
 * same data as text.
 */
export function LikertChart({
  itemNames,
  summaries,
  scale,
  labels,
  strings,
  fmt,
}: {
  itemNames: readonly string[];
  summaries: readonly LikertItemSummary[];
  scale: LikertScale;
  labels: readonly string[];
  strings: typeof LIKERT_SCALE_ANALYZER_STRINGS;
  fmt: NumberFormatter;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const layout = likertChartLayout(
    itemNames,
    summaries.map((summary) => divergingSegments(summary, scale)),
    labels,
    likertColors(scale.points)
  );
  const middle = layout.barHeight / 2 + 4;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <div className="rounded-lg border border-border bg-white p-2">
        <svg
          aria-labelledby="lk-chart-title lk-chart-desc"
          className="h-auto w-full"
          fontFamily={CHART_FONT}
          height={layout.height}
          ref={svgRef}
          role="img"
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          width={layout.width}
        >
          <title id="lk-chart-title">{strings.chartTitle}</title>
          <desc id="lk-chart-desc">{strings.chartDescription}</desc>
          <rect
            fill={CHART_INK.background}
            height={layout.height}
            width={layout.width}
          />
          {layout.legend.map((entry) => (
            <g key={entry.fill}>
              <rect
                fill={entry.fill}
                height={12}
                rx={2}
                width={12}
                x={entry.x}
                y={entry.y}
              />
              <text
                fill={CHART_INK.text}
                fontSize={12}
                x={entry.x + 18}
                y={entry.y + 10}
              >
                {entry.label}
              </text>
            </g>
          ))}
          {layout.ticks.map((tick) => (
            <g key={tick.value}>
              <line
                stroke={tick.value === 0 ? CHART_INK.axis : CHART_INK.grid}
                strokeWidth={tick.value === 0 ? 1.5 : 1}
                x1={tick.x}
                x2={tick.x}
                y1={layout.plotTop - 4}
                y2={layout.plotBottom}
              />
              <text
                fill={CHART_INK.muted}
                fontSize={11}
                textAnchor="middle"
                x={tick.x}
                y={layout.plotBottom + 18}
              >
                {percentText(fmt, Math.abs(tick.value), 0)}
              </text>
            </g>
          ))}
          {layout.rows.map((row) => (
            <g key={row.name}>
              <text
                fill={CHART_INK.text}
                fontSize={12}
                textAnchor="end"
                x={layout.labelX}
                y={row.y + middle}
              >
                {row.label}
              </text>
              {row.segments.map((segment, option) =>
                segment.width > 0 ? (
                  <g key={segment.code}>
                    <rect
                      fill={segment.fill}
                      height={layout.barHeight}
                      stroke={CHART_INK.background}
                      strokeWidth={2}
                      width={segment.width}
                      x={segment.x}
                      y={row.y}
                    >
                      <title>
                        {t(strings.segmentTooltip, {
                          item: row.name,
                          label: labels[option],
                          percent: percentText(fmt, segment.share),
                          count: fmt(segment.count),
                        })}
                      </title>
                    </rect>
                    {segment.labelled ? (
                      <text
                        fill={segment.ink}
                        fontSize={11}
                        pointerEvents="none"
                        textAnchor="middle"
                        x={segment.x + segment.width / 2}
                        y={row.y + middle}
                      >
                        {percentText(fmt, segment.share, 0)}
                      </text>
                    ) : null}
                  </g>
                ) : null
              )}
            </g>
          ))}
        </svg>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            if (svgRef.current) {
              downloadSvg(strings.chartFileSvg, svgRef.current);
            }
          }}
          type="button"
        >
          {strings.downloadSvg}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            if (svgRef.current) {
              downloadPng(strings.chartFilePng, svgRef.current).catch(
                () => undefined
              );
            }
          }}
          type="button"
        >
          {strings.downloadPng}
        </button>
      </div>
    </div>
  );
}
